import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Camera, Upload, Zap, Flashlight, SwitchCamera, Trash2, Check, Images, Minus, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { api, formatErr, cardImg, gbp, vLabel } from "@/lib/api";
import { Btn } from "@/components/UI";
import { CardImage } from "@/components/CardTile";
import { useCamera, grabFrame, useSteady, beep, GUIDE_H, STEADY_TEXT } from "@/components/camera";
import { useScanQueue, successfulScanKeys } from "@/hooks/useScanQueue";

const SCAN_TIMEOUT_MS = 120000;
const DUPLICATE_COOLDOWN_MS = 8000;

async function recognise(blob) {
  const fd = new FormData();
  fd.append("file", blob, "scan.jpg");
  const { data } = await api.post("/scan", fd, { timeout: SCAN_TIMEOUT_MS });
  return data;
}

function QueueItem({ it, onChange, onRemove, disabled }) {
  const m = it.matches?.[it.sel];
  return (
    <div className="rise flex gap-3 rounded-2xl border border-slate-200/70 bg-white p-3" data-testid={`batch-item-${it.key}`}>
      <img src={it.thumb} alt="scan" className="h-24 w-[68px] shrink-0 rounded-lg object-cover" />
      {m ? <div className="w-[68px] shrink-0"><CardImage image={m.image} name={m.name} /></div> : <div className="h-24 w-[68px] shrink-0 rounded-lg bg-slate-100" />}
      <div className="min-w-0 flex-1">
        {it.status === "scanning" && <p className="animate-pulse font-display font-bold text-slate-500">Recognising…</p>}
        {it.status === "error" && <p className="font-bold text-red-600">Scan failed</p>}
        {it.status === "done" && !m && <p className="font-bold text-amber-600">No match — try again</p>}
        {m && (
          <>
            <p className="truncate font-display font-bold">{m.name}</p>
            <p className="truncate text-xs text-slate-500">{m.set_name} · #{m.local_id} · {gbp(m.price_gbp)}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select disabled={disabled} value={it.sel} onChange={(e) => onChange({ sel: Number(e.target.value), variant: it.matches[e.target.value].variants?.[0] || "normal" })}
                className="max-w-[170px] rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold" data-testid={`batch-item-match-${it.key}`}>
                {it.matches.map((x, i) => <option key={x.card_id} value={i}>{`${x.name} · ${x.set_name} #${x.local_id}`}</option>)}
              </select>
              <select disabled={disabled} value={it.variant} onChange={(e) => onChange({ variant: e.target.value })} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold" data-testid={`batch-item-variant-${it.key}`}>
                {(m.variants || ["normal", "holo", "reverse"]).map((v) => <option key={v} value={v}>{vLabel(v)}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <button disabled={disabled} onClick={() => onChange({ qty: Math.max(1, it.qty - 1) })} className="rounded-full border p-1"><Minus className="h-3 w-3" /></button>
                <span className="w-5 text-center text-sm font-black">{it.qty}</span>
                <button disabled={disabled} onClick={() => onChange({ qty: it.qty + 1 })} className="rounded-full border p-1"><Plus className="h-3 w-3" /></button>
              </div>
            </div>
            {it.confidence !== "high" && <p className="mt-1 text-[11px] font-bold text-amber-600">Check this one — {it.confidence} confidence</p>}
          </>
        )}
      </div>
      <button disabled={disabled} onClick={onRemove} className="self-start rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`batch-item-remove-${it.key}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

function SingleResult({ res, onAdd }) {
  if (!res) return null;
  if (!res.matches.length) return <p className="rounded-2xl bg-amber-50 p-4 font-semibold text-amber-700" data-testid="scan-no-match">Couldn't recognise that card. Fill the frame with the card, avoid glare, and try again.</p>;
  return (
    <div className="space-y-3" data-testid="scan-result">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Best matches · {res.confidence} confidence {res.ocr?.numbers?.length ? `· read ${res.ocr.numbers[0]}` : ""}</p>
      {res.matches.map((m, i) => (
        <div key={m.card_id} className={`flex items-center gap-3 rounded-2xl border bg-white p-2 ${i === 0 ? "border-[#E3350D]" : "border-slate-200"}`} data-testid={`scan-match-${i}`}>
          <div className="w-14 shrink-0"><CardImage image={m.image} name={m.name} /></div>
          <div className="min-w-0 flex-1"><p className="truncate font-display font-bold">{m.name}</p><p className="truncate text-xs text-slate-500">{m.set_name} · #{m.local_id}</p></div>
          <Link to={`/card/${m.card_id}`} className="rounded-full p-2 hover:bg-slate-100" data-testid={`scan-match-open-${i}`}><ExternalLink className="h-4 w-4" /></Link>
          <button onClick={() => onAdd(m)} className="press rounded-full bg-[#E3350D] p-2 text-white" data-testid={`scan-match-add-${i}`}><Plus className="h-4 w-4" strokeWidth={3} /></button>
        </div>
      ))}
    </div>
  );
}

export default function Scan() {
  const nav = useNavigate();
  const videoRef = useRef(null);
  const [deviceId, setDeviceId] = useState("");
  const { state: cam, caps, devices, apply, mirrored } = useCamera(videoRef, deviceId);
  const [torch, setTorch] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [dest, setDest] = useState("");
  const [locs, setLocs] = useState([]);
  useEffect(() => {
    const controller = new AbortController();
    api.get("/locations", { signal: controller.signal }).then((r) => { if (!controller.signal.aborted) setLocs(r.data.locations); })
      .catch((e) => { if (!controller.signal.aborted) toast.error(formatErr(e)); });
    return () => controller.abort();
  }, []);
  const [mode, setMode] = useState("single");
  const [busy, setBusy] = useState(false);
  const [scanNotice, setScanNotice] = useState("");
  const [res, setRes] = useState(null);
  const { queue, setQueue, enqueue, patch } = useScanQueue();
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false);
  const [auto, setAuto] = useState(true);
  const [autoOpen, setAutoOpen] = useState(true);
  const last = useRef({ id: null, t: 0 });
  const busyRef = useRef(false);
  const capturingRef = useRef(false);

  const processBlob = useCallback(async (blob, automatic = false) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setScanNotice("");
    try {
      if (mode === "single") {
        setRes(null);
        const r = await recognise(blob);
        setRes(r);
        if (autoOpen && r.confidence === "high" && r.matches[0]) nav(`/card/${r.matches[0].card_id}`);
        return;
      }
      const key = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      enqueue(key, blob);
      try {
        const r = await recognise(blob);
        const top = r.matches[0];
        if (top && automatic && top.card_id === last.current.id && Date.now() - last.current.t < DUPLICATE_COOLDOWN_MS) {
          setQueue((q) => q.filter((x) => x.key !== key));
          setScanNotice("Already scanned · move to the next card");
          return;
        }
        if (top) { last.current = { id: top.card_id, t: Date.now() }; navigator.vibrate?.(40); beep(); }
        patch(key, { status: "done", matches: r.matches, confidence: r.confidence, variant: top?.variants?.[0] || "normal" });
        setScanNotice(top ? "Review the suggested match in the queue, then move to the next card" : "No match · adjust the card or lighting and try Capture again");
      } catch {
        patch(key, { status: "error" });
        setScanNotice("Scan failed · try Capture again");
      }
    } catch (e) {
      setScanNotice("Scan failed · try Capture again");
      toast.error(formatErr(e));
    } finally { busyRef.current = false; setBusy(false); }
  }, [mode, autoOpen, nav, enqueue, patch, setQueue]);

  const capture = useCallback(async (automatic = false) => {
    if (busyRef.current || capturingRef.current || !videoRef.current) return;
    capturingRef.current = true;
    try { const b = await grabFrame(videoRef.current); if (b) await processBlob(b, automatic === true); }
    catch (e) { toast.error(formatErr(e)); }
    finally { capturingRef.current = false; }
  }, [processBlob]);

  const autoCapture = useCallback(() => capture(true), [capture]);
  const steady = useSteady(videoRef, auto && cam === "ready" && !(mode === "single" && (busy || res)), autoCapture);
  const statusText = busy ? "Recognising captured photo…" : mode === "single" && res
    ? "Review the result · choose Scan another to continue"
    : scanNotice && (steady === "waiting" || steady === "idle") ? scanNotice
    : auto ? STEADY_TEXT[steady] || "Position a card inside the frame" : "Ready · position a card and press Capture";

  const onFiles = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    for (const f of mode === "single" ? files.slice(0, 1) : files) await processBlob(f);
  };
  const addOne = async (m) => {
    try { await api.post("/collection/add", { card_id: m.card_id, variant: m.variants?.[0] || "normal", location_id: dest || undefined }); toast.success(`Added ${m.name}`); } catch (e) { toast.error(formatErr(e)); }
  };
  const ready = queue.filter((x) => x.status === "done" && x.matches[x.sel]);
  const addAll = async () => {
    if (addingRef.current || !ready.length) return;
    addingRef.current = true;
    setAdding(true);
    try {
      const { data } = await api.post("/collection/batch", { location_id: dest || undefined, items: ready.map((x) => ({ card_id: x.matches[x.sel].card_id, variant: x.variant, quantity: x.qty })) });
      const successful = successfulScanKeys(ready, data);
      if (data.added > 0) {
        toast.success(`Added ${data.added} cards to your collection!`);
        confetti({ particleCount: 150, spread: 90 });
      }
      if (successful.size < ready.length) toast.error("Some cards could not be added. They remain in the queue for retry.");
      setQueue((q) => q.filter((x) => !successful.has(x.key)));
    } catch (e) { toast.error(formatErr(e)); }
    finally { addingRef.current = false; setAdding(false); }
  };

  return (
    <div data-testid="scan-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">Scan cards</h1>
          <p className="mt-2 text-slate-600">Hold a card inside the frame. Recognition runs on your own server.</p>
        </div>
        <div className="flex rounded-full bg-white p-1 shadow-sm" data-testid="scan-mode-toggle">
          {["single", "batch"].map((m) => (
            <button key={m} disabled={busy} onClick={() => { setMode(m); setRes(null); setScanNotice(""); }} data-testid={`scan-mode-${m}`}
              className={`press rounded-full px-5 py-2 font-display font-bold capitalize ${mode === m ? "bg-slate-900 text-white" : "text-slate-600"}`}>{m === "batch" ? "Batch mode" : "Single"}</button>
          ))}
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,480px)_1fr]">
        <div className="space-y-4">
          <div className="relative mx-auto aspect-[3/4] w-full max-w-[480px] overflow-hidden rounded-[28px] bg-slate-900 shadow-[0_20px_50px_rgba(0,0,0,0.3)]" data-testid="camera-view">
            <video ref={videoRef} playsInline muted className="h-full w-full object-cover" style={{ transform: mirrored ? "scaleX(-1)" : undefined }} />
            {cam !== "ready" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-8 text-center text-white">
                <Camera className="h-10 w-10 opacity-70" />
                <p className="font-display text-lg font-bold" data-testid="camera-status">
                  {cam === "idle" ? "Starting camera…" : cam === "denied" ? "Camera permission denied" : "Live camera needs HTTPS or localhost"}
                </p>
                <p className="text-sm text-white/70">You can still take or upload photos below — they work everywhere.</p>
              </div>
            )}
            {cam === "ready" && (
              <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl border-[3px] border-dashed border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
                style={{ height: `${GUIDE_H * 100}%`, aspectRatio: "63/88" }}>
                {busy && <div className="scan-beam" />}
              </div>
            )}
            {auto && cam === "ready" && <span className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 text-xs font-black text-white">AUTO SNAP ON</span>}
            {cam === "ready" && <p role="status" className={`absolute inset-x-4 bottom-4 rounded-2xl px-4 py-2 text-center text-sm font-bold backdrop-blur ${busy ? "bg-[#E3350D]/90 text-white" : steady === "blurry" ? "bg-amber-400/90 text-slate-900" : "bg-black/60 text-white"}`} data-testid="steady-status">{statusText}</p>}
            {cam === "ready" && (
              <div className="absolute right-3 top-3 flex flex-col gap-2">
                {caps.torch && <button onClick={() => { apply({ torch: !torch }); setTorch(!torch); }} className={`rounded-full p-2.5 ${torch ? "bg-[#FFCC00] text-slate-900" : "bg-black/50 text-white"}`} data-testid="torch-toggle" aria-label="Torch"><Flashlight className="h-5 w-5" /></button>}
                {devices.length > 1 && <button onClick={() => { const i = devices.findIndex((d) => d.deviceId === deviceId); setDeviceId(devices[(i + 1) % devices.length].deviceId); }} className="rounded-full bg-black/50 p-2.5 text-white" data-testid="switch-camera-button" aria-label="Switch camera"><SwitchCamera className="h-5 w-5" /></button>}
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Btn onClick={capture} disabled={cam !== "ready" || busy} className="px-8 py-3 text-lg" data-testid="capture-button">
              <Camera className="h-5 w-5" /> {busy ? "Scanning…" : "Capture"}
            </Btn>
            <Btn variant={auto ? "yellow" : "ghost"} onClick={() => setAuto(!auto)} disabled={cam !== "ready"} data-testid="auto-scan-toggle"><Zap className="h-5 w-5" /> Auto snap {auto ? "on" : "off"}</Btn>
            <label className="press inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 font-display font-bold" data-testid="take-photo-button">
              <Upload className="h-5 w-5" /> Take photo
              <input disabled={busy} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFiles} data-testid="take-photo-input" />
            </label>
            <label className="press inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 font-display font-bold" data-testid="upload-photos-button">
              <Images className="h-5 w-5" /> Upload {mode === "batch" ? "photos" : "photo"}
              <input disabled={busy} type="file" accept="image/*" multiple={mode === "batch"} className="hidden" onChange={onFiles} data-testid="upload-photos-input" />
            </label>
          </div>
          {cam === "ready" && caps.zoom && (
            <label className="flex items-center gap-3 text-sm font-semibold text-slate-600">Zoom
              <input type="range" min={caps.zoom.min} max={Math.min(caps.zoom.max, 5)} step={caps.zoom.step || 0.1} value={zoom ?? caps.zoom.min} onChange={(e) => { const z = Number(e.target.value); setZoom(z); apply({ zoom: z }); }} className="flex-1" data-testid="zoom-slider" />
            </label>
          )}
          <select value={dest} onChange={(e) => setDest(e.target.value)} className="h-11 w-full rounded-full border border-slate-200 bg-white px-4 text-sm font-bold" data-testid="scan-destination-select">
            <option value="">Add cards to: no storage yet</option>{locs.map((l) => <option key={l.id} value={l.id}>{`Add cards to: ${l.name}`}</option>)}
          </select>
          {mode === "single" && (
            <label className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-600">
              <input type="checkbox" checked={autoOpen} onChange={(e) => setAutoOpen(e.target.checked)} data-testid="auto-open-checkbox" /> Open card page automatically when sure
            </label>
          )}
        </div>
        <div>
          {mode === "single" ? (
            busy ? <p className="animate-pulse font-display text-lg font-bold text-slate-500">Recognising card…</p> : (
              <div className="space-y-3"><SingleResult res={res} onAdd={addOne} />{res && <Btn variant="dark" onClick={() => setRes(null)} data-testid="scan-again-button"><Camera className="h-4 w-4" /> Scan another</Btn>}</div>
            )
          ) : (
            <div className="space-y-3" data-testid="batch-queue">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-display text-xl font-bold">Scanned: {queue.length}</p>
                <div className="flex gap-2">
                  {queue.length > 0 && <Btn variant="ghost" disabled={adding} onClick={() => setQueue([])} data-testid="batch-clear-button">Clear</Btn>}
                  <Btn onClick={addAll} disabled={!ready.length || adding} data-testid="batch-add-all-button"><Check className="h-5 w-5" /> {adding ? "Adding…" : `Add ${ready.length} to collection`}</Btn>
                </div>
              </div>
              {queue.length === 0 && <p className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center text-slate-500">Turn on <b>Auto</b> and slide cards under the camera one after another, or upload a bunch of photos at once.</p>}
              {queue.map((it) => <QueueItem key={it.key} it={it} disabled={adding} onChange={(p) => patch(it.key, p)} onRemove={() => setQueue((q) => q.filter((x) => x.key !== it.key))} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
